import { useMemo } from 'react';
import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import { ContactType_Enum } from '@weave/schema-gen-ts/dist/shared/persons/v3/enums.pb';
import { PaymentMethodStatus } from '@frontend/api-payment-plans';
import { PaymentMethodsBaseApiMethods } from '../types/api.types';

export type UsePaymentMethodsDerivedState = {
  personId: string | undefined;
  person: Person | undefined;
  locationId: string | undefined;
  useGetPaymentMethodsByPerson: PaymentMethodsBaseApiMethods['useGetPaymentMethodsByPerson'];
  useQueryLegacyPerson: PaymentMethodsBaseApiMethods['useQueryLegacyPerson'];
};

export const usePaymentMethodsDerivedState = ({
  personId,
  person: propsPerson,
  locationId,
  useGetPaymentMethodsByPerson,
  useQueryLegacyPerson,
}: UsePaymentMethodsDerivedState) => {
  const personQuery = useQueryLegacyPerson({
    locationId,
    personId: propsPerson?.personId || personId,
  });

  const person = useMemo(() => {
    if (propsPerson) {
      return propsPerson;
    }
    return personQuery.data ? personQuery.data : null;
  }, [propsPerson, personQuery.data]);

  const {
    data: paymentMethodsQuery,
    isLoading: isLoadingPaymentMethods,
    isRefetching: isRefetchingPaymentMethods,
    refetch: refetchPaymentMethodsAsync,
  } = useGetPaymentMethodsByPerson({
    locationId: locationId ?? '',
    patientId: person?.personId || '',
  });

  const paymentMethods = useMemo(() => {
    return paymentMethodsQuery || [];
  }, [paymentMethodsQuery]);

  const defaultPaymentMethod = useMemo(() => {
    const filteredPaymentMethods = paymentMethods?.filter(
      (pm) =>
        !(
          pm.status &&
          [
            PaymentMethodStatus.PAYMENT_METHOD_STATUS_VERIFICATION_PENDING,
            PaymentMethodStatus.PAYMENT_METHOD_STATUS_EXPIRED,
          ].includes(pm.status)
        )
    );

    const foundValidDefault = filteredPaymentMethods?.find(
      (pm) => (pm.is_default && pm.type === 'card') || (pm.is_default && pm.type === 'us_bank_account')
    );
    return foundValidDefault || filteredPaymentMethods?.find((pm) => pm.type === 'card');
  }, [paymentMethods]);

  const personEmail = useMemo(() => {
    return person?.contactInfo?.find(({ type }) => type === ContactType_Enum.EMAIL)?.destination || '';
  }, [person]);

  const authorizationEmail = useMemo(() => {
    return (
      paymentMethods?.findLast(
        (pm) => pm.status === PaymentMethodStatus.PAYMENT_METHOD_STATUS_VERIFICATION_PENDING && pm.authorizationEmail
      )?.authorizationEmail || ''
    );
  }, [paymentMethods]);

  return {
    person,
    paymentMethods,
    defaultPaymentMethod,
    isLoadingPaymentMethods,
    isRefetchingPaymentMethods,
    refetchPaymentMethodsAsync,
    authorizationEmail,
    personEmail,
  };
};
